import { useCallback, useEffect, useRef, useState, type CSSProperties, type PointerEvent } from 'react';
import { cn } from '@/lib/utils';
import { BannerLayerRender, layerBoxStyle } from '@/components/theme/BannerLayerRender';
import { BANNER_H, BANNER_W, type BannerEditing, type BannerLayer } from '../model/bannerLayers';

/**
 * The Banner Studio canvas: the 1600×600 design space rendered live, with
 * Figma-style selection, drag, corner-resize, and center/edge snapping. All
 * geometry is stored in design px; pointer deltas divide by the render scale.
 * Store-agnostic — both studios drive it through the BannerEditing accessors.
 */

const SNAP = 12; // design px
const HANDLES = ['nw', 'ne', 'sw', 'se'] as const;
type Handle = (typeof HANDLES)[number];

export function BannerLayerCanvas({ editing }: { editing: BannerEditing }) {
  const { layers, selectedId, onSelect, onPatchLayer } = editing;
  const design = { w: BANNER_W, h: BANNER_H, layers } as const;
  const [guides, setGuides] = useState<{ v: number | null; h: number | null }>({ v: null, h: null });
  const frameRef = useRef<HTMLDivElement>(null);
  const editingRef = useRef(editing);
  editingRef.current = editing;
  const drag = useRef<{
    id: string;
    mode: 'move' | Handle;
    startX: number;
    startY: number;
    box: { x: number; y: number; w: number; h: number };
  } | null>(null);

  const selected = layers.find((l) => l.id === selectedId) ?? null;

  const scale = useCallback((): number => {
    const el = frameRef.current;
    return el ? el.clientWidth / BANNER_W : 1;
  }, []);

  function beginDrag(e: PointerEvent, layer: BannerLayer, mode: 'move' | Handle): void {
    if (layer.locked) return;
    e.stopPropagation();
    (e.target as HTMLElement).setPointerCapture(e.pointerId);
    onSelect(layer.id);
    drag.current = {
      id: layer.id,
      mode,
      startX: e.clientX,
      startY: e.clientY,
      box: { x: layer.x, y: layer.y, w: layer.w, h: layer.h },
    };
  }

  function onPointerMove(e: PointerEvent): void {
    const d = drag.current;
    if (!d) return;
    const k = scale();
    const dx = (e.clientX - d.startX) / k;
    const dy = (e.clientY - d.startY) / k;
    let { x, y, w, h } = d.box;

    if (d.mode === 'move') {
      x += dx;
      y += dy;
      const snapped = snapBox(x, y, w, h);
      x = snapped.x;
      y = snapped.y;
      setGuides(snapped.guides);
    } else {
      if (d.mode.includes('e')) w = Math.max(16, d.box.w + dx);
      if (d.mode.includes('s')) h = Math.max(16, d.box.h + dy);
      if (d.mode.includes('w')) {
        w = Math.max(16, d.box.w - dx);
        x = d.box.x + (d.box.w - w);
      }
      if (d.mode.includes('n')) {
        h = Math.max(16, d.box.h - dy);
        y = d.box.y + (d.box.h - h);
      }
    }
    onPatchLayer(d.id, { x, y, w, h }, `geom-${d.id}`);
  }

  function endDrag(): void {
    drag.current = null;
    setGuides({ v: null, h: null });
  }

  // Delete / arrow-nudge for the selected layer.
  useEffect(() => {
    function onKey(e: KeyboardEvent): void {
      const target = e.target as HTMLElement | null;
      if (target instanceof HTMLInputElement || target instanceof HTMLTextAreaElement || target?.isContentEditable) return;
      const ed = editingRef.current;
      const layer = ed.layers.find((l) => l.id === ed.selectedId);
      if (!layer || layer.locked) return;
      if (e.key === 'Delete' || e.key === 'Backspace') {
        e.preventDefault();
        ed.onSetLayers(ed.layers.filter((l) => l.id !== layer.id));
        ed.onSelect(null);
      } else if (e.key.startsWith('Arrow')) {
        e.preventDefault();
        const step = e.shiftKey ? 16 : 2;
        const dx = e.key === 'ArrowLeft' ? -step : e.key === 'ArrowRight' ? step : 0;
        const dy = e.key === 'ArrowUp' ? -step : e.key === 'ArrowDown' ? step : 0;
        ed.onPatchLayer(layer.id, { x: layer.x + dx, y: layer.y + dy }, `nudge-${layer.id}`);
      }
    }
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, []);

  void editing.onSetLayers; // Delete-key path uses the ref; keep the accessor part of the contract.

  return (
    <div className="flex min-h-0 flex-1 flex-col items-center overflow-auto bg-ink-950 p-6 pt-4">
      <div className="mb-2.5 flex w-full max-w-[980px] items-center justify-between">
        <span className="text-[11px] font-bold text-white/35">1600 × 600 — banner design space</span>
        {selected && (
          <span className="text-[11px] font-semibold text-white/30">
            {selected.name} · drag to move · corners resize · Del removes
          </span>
        )}
      </div>

      <div
        ref={frameRef}
        className="relative w-full max-w-[980px] flex-none touch-none select-none overflow-hidden rounded-xl border border-white/[0.1] bg-ink-950 shadow-[0_30px_80px_rgba(0,0,0,0.5)]"
        style={{ aspectRatio: `${BANNER_W} / ${BANNER_H}` }}
        onPointerDown={() => onSelect(null)}
        onPointerMove={onPointerMove}
        onPointerUp={endDrag}
        onPointerCancel={endDrag}
      >
        {/* checker base so transparency reads */}
        <div className="absolute inset-0 opacity-[0.05] [background-image:repeating-conic-gradient(#fff_0%_25%,transparent_0%_50%)] [background-size:24px_24px]" />

        <BannerLayerRender design={design} />

        {/* interaction plane — one hit target per visible layer */}
        {layers.map((layer) => {
          if (layer.hidden) return null;
          const active = layer.id === selectedId;
          return (
            <div
              key={layer.id}
              style={layerBoxStyle(layer) as CSSProperties}
              className={cn(
                layer.locked ? 'cursor-not-allowed' : 'cursor-move',
                active
                  ? 'outline outline-[1.5px] outline-accent'
                  : 'hover:outline hover:outline-1 hover:outline-white/30',
              )}
              onPointerDown={(e) => beginDrag(e, layer, 'move')}
            >
              {active &&
                !layer.locked &&
                HANDLES.map((h) => (
                  <span
                    key={h}
                    onPointerDown={(e) => beginDrag(e, layer, h)}
                    className={cn(
                      'absolute z-10 h-2.5 w-2.5 rounded-[2px] border border-accent bg-white',
                      h === 'nw' && '-left-1.5 -top-1.5 cursor-nwse-resize',
                      h === 'ne' && '-right-1.5 -top-1.5 cursor-nesw-resize',
                      h === 'sw' && '-bottom-1.5 -left-1.5 cursor-nesw-resize',
                      h === 'se' && '-bottom-1.5 -right-1.5 cursor-nwse-resize',
                    )}
                  />
                ))}
            </div>
          );
        })}

        {/* snap guides */}
        {guides.v != null && (
          <span className="pointer-events-none absolute bottom-0 top-0 w-px bg-accent" style={{ left: `${(guides.v / BANNER_W) * 100}%` }} />
        )}
        {guides.h != null && (
          <span className="pointer-events-none absolute left-0 right-0 h-px bg-accent" style={{ top: `${(guides.h / BANNER_H) * 100}%` }} />
        )}
      </div>
    </div>
  );
}

function snapBox(
  x: number,
  y: number,
  w: number,
  h: number,
): { x: number; y: number; guides: { v: number | null; h: number | null } } {
  let gv: number | null = null;
  let gh: number | null = null;
  const cx = x + w / 2;
  const cy = y + h / 2;

  if (Math.abs(cx - BANNER_W / 2) < SNAP) {
    x = BANNER_W / 2 - w / 2;
    gv = BANNER_W / 2;
  } else if (Math.abs(x) < SNAP) {
    x = 0;
    gv = 0;
  } else if (Math.abs(x + w - BANNER_W) < SNAP) {
    x = BANNER_W - w;
    gv = BANNER_W;
  }
  if (Math.abs(cy - BANNER_H / 2) < SNAP) {
    y = BANNER_H / 2 - h / 2;
    gh = BANNER_H / 2;
  } else if (Math.abs(y) < SNAP) {
    y = 0;
    gh = 0;
  } else if (Math.abs(y + h - BANNER_H) < SNAP) {
    y = BANNER_H - h;
    gh = BANNER_H;
  }
  return { x, y, guides: { v: gv, h: gh } };
}
